// MACHINERY V2 (Stroje) — the fleet's PAGE-LEVEL overlays (split out of
// FleetPage.tsx at M2.13).
//
// The readiness popup, the add-machine picker, the toast host and the dialog
// bundle. They are grouped here for the same reason FleetContent exists: the
// page was at 290 of 300 lines and M2.13 adds to it, so the near-cap rule
// requires a split before new lines land. Nothing changed in the move —
// including, deliberately, WHERE these mount.
//
// >>> MOUNT DEPTH IS THE GUARD, AND IT SURVIVES THIS SPLIT. <<< These render as
// siblings of the rail/main flex row, deliberately outside every card AND every
// row: the clickable ancestor is then not even a React ancestor of the popup, so
// a click on Close / the backdrop / a CTA cannot navigate. ReadinessPopupV2 ALSO
// carries the standing stopPropagation guard on BOTH Dialog.Overlay and
// Dialog.Content, which is what protects the in-card and in-row cases. Keep this
// component mounted at page level; nesting it inside a body would re-open both.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import AddMachinePicker from "../add/AddMachinePicker";
import MachineryToastHost from "../feedback/MachineryToastHost";
import ReadinessPopupV2 from "../readiness/ReadinessPopupV2";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import FleetDialogs, { FleetDialogsProps } from "./FleetDialogs";
import { FleetDialogControls } from "./useFleetDialogs";

export interface FleetOverlaysProps {
  readinessMachine: Machine | null;
  readinessPresentation: MachineryV2ReadinessPresentation | undefined;
  onCloseReadiness: () => void;
  addPickerOpen: boolean;
  onCloseAddPicker: () => void;
  onCreated: (id?: string) => void;
  onCatalogModel: FleetDialogControls["openCatalogAdd"];
  selectedMachine?: Machine;
  dialogProps: Omit<FleetDialogsProps, "selectedMachine">;
}

const FleetOverlays: React.FC<FleetOverlaysProps> = ({
  readinessMachine, readinessPresentation, onCloseReadiness, addPickerOpen,
  onCloseAddPicker, onCreated, onCatalogModel, selectedMachine, dialogProps,
}) => (
  <>
    {readinessMachine && readinessPresentation && (
      <ReadinessPopupV2
        open
        onOpenChange={(o) => {
          if (!o) onCloseReadiness();
        }}
        presentation={readinessPresentation}
        machineOrModelName={getMachineDisplayName(readinessMachine)}
        oemPartnerName={readinessMachine.catalogModel?.oemPartnerName}
        manufacturerName={readinessMachine.manufacturer?.name}
      />
    )}

    {/* M2.7 — the v2 add flow replaces MachineDialog's hub + custom form.
        The REFRESH lives in the page, not in the dialog: v1 fires getMachines
        from inside the dialog (MachineDialog.tsx:374), but every v2 mutation
        refresh goes through `fleetFetchQuery` (fleetFetchQuery.ts) and the page
        owns the query ref. The dialog closes only after createMachine resolved
        (§9 D7), so `onCreated` runs on success only. */}
    <AddMachinePicker
      open={addPickerOpen}
      onClose={onCloseAddPicker}
      onCreated={onCreated}
      onCatalogModel={onCatalogModel}
    />

    {/* The sanctioned v2 feedback primitive (review F2). */}
    <MachineryToastHost />

    <FleetDialogs selectedMachine={selectedMachine} {...dialogProps} />
  </>
);

export default FleetOverlays;
